import React from "react";
import { CHAIN_PALETTES, detectChain } from "@/lib/chainPalette";

/**
 * DispensaryCover — inline SVG storefront illustration used for dispensary
 * cover photos. NEVER rotates (purely client-rendered, no CDN). Looks like
 * a stylized dispensary banner with the chain colors, store name, and a
 * subtle cannabis-leaf motif.
 *
 * Per user: NO photos that can rotate to cars/birds/abstract. SVG only.
 *
 * The chain palette is shared with `DispensaryDetail` via `lib/chainPalette`
 * so the SVG art and the HTML header chrome stay tonally locked together.
 */

// djb2 hash — used to pick a variant detail per dispensary slug
const hashStr = (s) => {
  let h = 5381;
  const str = String(s || "");
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) | 0;
  return Math.abs(h);
};

// Greedy word-wrap for the SVG name. SVG <text> doesn't wrap natively, so
// we manually split a long name into at most 2 lines so it never spills
// off the right edge of the cropped cover. If the final word still won't
// fit, we ellipsize.
function wrapName(text, maxChars) {
  const words = String(text || "").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [text];
  const lines = [];
  let current = "";
  for (const w of words) {
    if (lines.length >= 2) break; // hard-cap at 2 lines
    const tentative = current ? `${current} ${w}` : w;
    if (tentative.length <= maxChars) {
      current = tentative;
    } else {
      if (current) lines.push(current);
      current = w;
    }
  }
  if (current) lines.push(current);
  // If we still have words left over, ellipsize the last line
  const used = lines.join(" ").split(/\s+/).filter(Boolean).length;
  if (used < words.length) {
    const last = lines[lines.length - 1];
    lines[lines.length - 1] =
      last.length > maxChars - 1 ? `${last.slice(0, maxChars - 1)}…` : `${last}…`;
  }
  return lines.length ? lines : [text];
}

export const DispensaryCover = ({
  name = "",
  slug = "",
  city = "",
  posHint = "",
  className = "",
  testId,
  // When the parent renders a real HTML <h1> on top of this SVG (the
  // dispensary detail page) we suppress the in-SVG name to avoid the
  // duplicate / "ghost" text artifact. Everywhere else (list cards, home
  // cards) we WANT the name in the SVG so the card has something to read.
  showName = true,
}) => {
  const chain = detectChain(name, slug, posHint);
  const palette = CHAIN_PALETTES[chain] || CHAIN_PALETTES.default;
  const h = hashStr(slug || name);

  // Two display lines for the name — first is the chain, second is the city
  // e.g. "Sunnyside" / "Butler, PA"
  const cityLine = city ? `${city.toUpperCase()}, PA` : "PENNSYLVANIA";
  const rawName = name.replace(/\s*[—–-]\s*.+$/, "").trim() || "Bud Link";
  const nameLines = wrapName(rawName, 14);
  // Auto-shrink font when name is long. SVG renders into a viewBox of
  // 1600×600 but the parent card crops with `slice`, so the *visible*
  // width sits ~1100px in SVG coords. Tuned to keep "Restore Integrative
  // Wellness" inside the card on mobile while leaving "Trulieve" looking
  // dramatic at full size.
  const nameFontSize = nameLines.length === 2 ? 64 : rawName.length > 10 ? 60 : 76;
  const nameLineHeight = 1.05; // em
  // Center the wrapped block roughly around y=320 so it stays balanced
  const firstLineY = nameLines.length === 2 ? 290 : 320;

  // Unique IDs per render
  const gid = `dc-g-${h}`;
  const nid = `dc-n-${h}`;

  return (
    <svg
      viewBox="0 0 1600 600"
      xmlns="http://www.w3.org/2000/svg"
      className={`w-full h-full ${className}`}
      preserveAspectRatio="xMinYMid slice"
      data-testid={testId || `dispensary-cover-${chain}`}
      role="img"
      aria-label={`${name || "Dispensary"} cover illustration`}
    >
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={palette.bg1} />
          <stop offset="100%" stopColor={palette.bg2} />
        </linearGradient>
        <pattern id={nid} x="0" y="0" width="80" height="80" patternUnits="userSpaceOnUse">
          <circle cx="6" cy="9" r="0.9" fill={palette.ink} opacity="0.05" />
          <circle cx="42" cy="22" r="0.7" fill={palette.ink} opacity="0.06" />
          <circle cx="62" cy="58" r="1" fill={palette.ink} opacity="0.05" />
          <circle cx="18" cy="62" r="0.8" fill={palette.ink} opacity="0.06" />
        </pattern>
      </defs>

      <rect width="1600" height="600" fill={`url(#${gid})`} />
      <rect width="1600" height="600" fill={`url(#${nid})`} />

      {/* Decorative cannabis-leaf silhouettes — large, low-opacity, off to the right */}
      <g opacity="0.18" fill={palette.leaf}>
        <g transform="translate(1280 100) scale(1.6)">
          <path d="M100 10 L106 80 L160 40 L130 120 L200 100 L130 170 L180 220 L100 200 L20 220 L70 170 L0 100 L70 120 L40 40 L94 80 Z" />
        </g>
        <g transform="translate(1120 -20) scale(0.9) rotate(15)" opacity="0.6">
          <path d="M100 10 L106 80 L160 40 L130 120 L200 100 L130 170 L180 220 L100 200 L20 220 L70 170 L0 100 L70 120 L40 40 L94 80 Z" />
        </g>
        <g transform="translate(1400 340) scale(0.7) rotate(-20)" opacity="0.55">
          <path d="M100 10 L106 80 L160 40 L130 120 L200 100 L130 170 L180 220 L100 200 L20 220 L70 170 L0 100 L70 120 L40 40 L94 80 Z" />
        </g>
      </g>

      {/* Gold trim line */}
      <line x1="80" y1="500" x2="380" y2="500" stroke={palette.accent} strokeWidth="2" opacity="0.85" />

      {/* Storefront copy. We always render the city eyebrow. The big chain
          name + tagline are gated behind `showName` because the dispensary
          *detail* page renders an HTML <h1> on top of this art (and we
          can't have both, or we get the ghost-text artifact). List cards
          and home cards always pass showName, so they get the full
          identity inside the cover. */}
      <g style={{ pointerEvents: "none" }}>
        <text
          x="80"
          y="180"
          fontFamily="Inter, ui-sans-serif, system-ui"
          fontSize="18"
          letterSpacing="6"
          fill={palette.accent}
          opacity={showName ? "0.95" : "0.65"}
        >
          ◆ {cityLine}
        </text>
        {showName && (
          <>
            <text
              x="80"
              y={firstLineY}
              fontFamily="Georgia, 'DM Serif Display', serif"
              fontSize={nameFontSize}
              fontWeight="600"
              fill={palette.ink}
              letterSpacing="-1"
            >
              {nameLines.map((ln, i) => (
                <tspan
                  key={i}
                  x="80"
                  dy={i === 0 ? 0 : `${nameLineHeight}em`}
                >
                  {ln}
                </tspan>
              ))}
            </text>
            <text
              x="80"
              y={firstLineY + nameFontSize * nameLineHeight * nameLines.length + 30}
              fontFamily="Inter, ui-sans-serif, system-ui"
              fontSize="22"
              fill={palette.ink}
              opacity="0.7"
            >
              Pickup · Live menu via Bud Link
            </text>
          </>
        )}
      </g>
    </svg>
  );
};

export default DispensaryCover;
